import React from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Grid,
  GridItem,
  HStack,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { BsArrowCounterclockwise, BsCheckCircle, BsLightningChargeFill, BsMagic, BsXCircle } from "react-icons/bs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import RiskReviewConfirmDialog from "../components/common/RiskReviewConfirmDialog";
import RiskReviewErrorState from "../components/common/RiskReviewErrorState";
import RiskReviewLoadingState from "../components/common/RiskReviewLoadingState";
import RiskReviewPageShell from "../components/common/RiskReviewPageShell";
import RiskReviewPanel from "../components/common/RiskReviewPanel";
import RiskReviewDynamicForm from "../components/form/RiskReviewDynamicForm";
import RiskReviewApprovalTimeline from "../components/requests/RiskReviewApprovalTimeline";
import RiskReviewRequestHeader from "../components/requests/RiskReviewRequestHeader";
import RiskReviewRequestSummary from "../components/requests/RiskReviewRequestSummary";
import RiskReviewRiskSummary from "../components/requests/RiskReviewRiskSummary";
import RiskReviewWorkflowSelection from "../components/requests/RiskReviewWorkflowSelection";
import { riskReviewKeys } from "../constants/riskReviewQueryKeys";
import { useRiskReviewFormEngine } from "../hooks/useRiskReviewFormEngine";
import { useRiskReviewRequest } from "../hooks/useRiskReviewRequest";
import riskReviewService from "../services/riskReviewService";
import {
  getRiskReviewErrorMessage,
  getTemplateDeclarationFieldCodes,
  getValidationErrors,
  hasTemplateDeclarationField,
  valuesArrayToMap,
} from "../utils/riskReviewFormatters";
import { buildRiskReviewDummyValues } from "../utils/riskReviewDummyData";
import { normalizeFieldValueForSubmit } from "../utils/riskReviewValueNormalizer";

const buildSubmitValues = (template, values) => {
  const fields = (template?.sections || []).flatMap((section) => section.fields || []);
  return fields.reduce((payload, field) => {
    if (field.readOnly || ["READ_ONLY", "CALCULATED", "FILE_UPLOAD", "EXCEL_UPLOAD"].includes(field.inputType)) {
      return payload;
    }
    payload[field.code] = normalizeFieldValueForSubmit(values[field.code], field.inputType);
    return payload;
  }, {});
};

const getDeclarationAcceptedForSave = ({ template, values, fallback }) => {
  const declarationCodes = getTemplateDeclarationFieldCodes(template);
  if (!declarationCodes.length) return fallback;
  return declarationCodes.some((fieldCode) => values[fieldCode] === true);
};

const parseWorkflowSelection = (selectedWorkflowId) => {
  if (!selectedWorkflowId) return {};
  const [workflowId, workflowVersion] = selectedWorkflowId.split(":v");
  return {
    workflowId: workflowId || undefined,
    workflowVersion: workflowVersion ? Number(workflowVersion) : undefined,
  };
};

const getLiveRisks = (template, values = {}) =>
  (template?.sections || [])
    .flatMap((section) =>
      (section.fields || []).map((field) => ({
        section,
        field,
      })),
    )
    .filter(({ field }) => {
      const config = field.riskConfig;
      if (!config?.enabled) return false;

      return values[field.code] === config.triggerValue;
    })
    .map(({ section, field }) => ({
      fieldCode: field.code,
      fieldLabel: field.label,
      sectionCode: section.code,
      sectionName: section.name,
      severity: field.riskConfig.severity,
      category: field.riskConfig.category,
      message: field.riskConfig.message,
      triggerCode: field.riskConfig.triggerCode,
    }));

const RiskReviewRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const submitDialog = useDisclosure();
  const actionDialog = useDisclosure();
  const query = useRiskReviewRequest(id);
  const runtime = query.data;
  const request = runtime?.request;
  const template = runtime?.template;
  const permissions = runtime?.permissions || {};
  const hasDeclarationInTemplate = hasTemplateDeclarationField(template);
  const form = useRiskReviewFormEngine({
    template,
    initialValues: runtime?.values || valuesArrayToMap(request?.values),
  });
  const liveValues = form.watch();
  const liveRisks = React.useMemo(
    () => getLiveRisks(template, liveValues),
    [template, liveValues],
  );
  const [declarationAccepted, setDeclarationAccepted] = React.useState(false);
  const [validationSummary, setValidationSummary] = React.useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState("");
  const [approvalAction, setApprovalAction] = React.useState(null);
  const [approvalComments, setApprovalComments] = React.useState("");

  React.useEffect(() => {
    setDeclarationAccepted(Boolean(request?.declarationAccepted));
  }, [request?.declarationAccepted]);

  React.useEffect(() => {
    if (!request?.workflowId) return;
    setSelectedWorkflowId(
      `${request.workflowId?._id || request.workflowId}:v${request.workflowVersion || request.workflowSnapshot?.version || 1}`,
    );
  }, [request?.workflowId, request?.workflowSnapshot?.version, request?.workflowVersion]);

  React.useEffect(() => {
    const beforeUnload = (event) => {
      if (!form.formState.isDirty) return;
      event.preventDefault();
      event.returnValue = "You have unsaved changes. Leave without saving?";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [form.formState.isDirty]);

  const saveMutation = useMutation({
    mutationFn: riskReviewService.updateRequest,
    onSuccess: (data) => {
      queryClient.setQueryData(riskReviewKeys.request(id), (current) => ({
        ...current,
        request: data,
        values: valuesArrayToMap(data?.values),
      }));
      form.reset(valuesArrayToMap(data?.values));
      toast({ title: "Draft saved", status: "success" });
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const submitMutation = useMutation({
    mutationFn: riskReviewService.submitRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.request(id) });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.requests() });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.dashboard() });
      toast({ title: "Request submitted", status: "success" });
      submitDialog.onClose();
    },
    onError: (error) => {
      const errors = getValidationErrors(error);
      setValidationSummary(errors);
      errors.forEach((item) => {
        if (item.fieldCode) {
          form.setError(item.fieldCode, {
            type: "server",
            message: item.message,
          });
        }
      });
      toast({ title: getRiskReviewErrorMessage(error), status: "error" });
    },
  });

  const revisionMutation = useMutation({
    mutationFn: riskReviewService.createRevision,
    onSuccess: (revision) => {
      toast({ title: "Revision created", status: "success" });
      navigate(`/risk-review/requests/${revision._id}`);
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: riskReviewService.withdrawRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.request(id) });
      toast({ title: "Request withdrawn", status: "success" });
    },
  });

  const approvalMutation = useMutation({
    mutationFn: async ({ action, comments }) => {
      if (action === "APPROVE") return riskReviewService.approve({ id: permissions.actionableApprovalId, comments });
      if (action === "REJECT") return riskReviewService.reject({ id: permissions.actionableApprovalId, comments });
      if (action === "SEND_BACK") return riskReviewService.sendBack({ requestId: id, comments });
      return riskReviewService.approveByBench({ requestId: id, comments });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.request(id) });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.approvals() });
      queryClient.invalidateQueries({ queryKey: riskReviewKeys.dashboard() });
      toast({ title: `${approvalAction === "SEND_BACK" ? "Request sent back" : approvalAction === "BENCH_APPROVE" ? "Request approved by bench" : `Request ${approvalAction?.toLowerCase()}`}`, status: "success" });
      setApprovalComments("");
      setApprovalAction(null);
      actionDialog.onClose();
    },
    onError: (error) => toast({ title: getRiskReviewErrorMessage(error), status: "error" }),
  });

  const openApprovalAction = (action) => {
    setApprovalAction(action);
    setApprovalComments("");
    actionDialog.onOpen();
  };

  const quickFillTestCase = () => {
    const values = buildRiskReviewDummyValues({
      template,
      visibility: form.rules?.visibility || {},
    });

    Object.entries(values).forEach(([fieldCode, value]) => {
      form.setValue(fieldCode, value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });

    if (!hasDeclarationInTemplate) {
      setDeclarationAccepted(true);
    }

    toast({
      title: "Test data filled",
      description: `${Object.keys(values).length} fields updated from the active template.`,
      status: "success",
    });
  };

  const saveDraft = async () => {
    const values = form.getValues();
    const payload = {
      values: buildSubmitValues(template, values),
      declarationAccepted: getDeclarationAcceptedForSave({
        template,
        values,
        fallback: declarationAccepted,
      }),
      ...parseWorkflowSelection(selectedWorkflowId),
    };
    return saveMutation.mutateAsync({ id, payload });
  };

  const submitReview = async () => {
    if (!selectedWorkflowId) {
      toast({
        title: "Select workflow",
        description: "Please select a workflow version before submitting this Risk Review.",
        status: "warning",
      });
      return;
    }

    await saveDraft();
    submitMutation.mutate(id);
  };

  if (query.isLoading) {
    return (
      <RiskReviewPageShell title="Risk Review Request">
        <RiskReviewLoadingState />
      </RiskReviewPageShell>
    );
  }

  if (query.isError) {
    return (
      <RiskReviewPageShell title="Risk Review Request">
        <RiskReviewErrorState error={query.error} onRetry={query.refetch} />
      </RiskReviewPageShell>
    );
  }

  return (
    <RiskReviewPageShell title="Risk Review Request" description="Review request details, complete the form, and manage workflow actions." pb={6}>
      <RiskReviewPanel p={5} borderRadius="xl" boxShadow="0 4px 14px rgba(16,24,40,0.06)">
        <RiskReviewRequestHeader request={request} />
      </RiskReviewPanel>
      <RiskReviewPanel p={3}>
        <HStack justify="space-between" spacing={3} wrap="wrap">
          <Text color="gray.500" fontSize="sm">
            {form.formState.isDirty ? "Unsaved changes" : "All changes saved"}
          </Text>
          <HStack spacing={2} wrap="wrap" justify="flex-end">
            {permissions.canApprove && permissions.actionableApprovalId && ["PENDING_APPROVAL", "SUBMITTED"].includes(request?.status) ? (
              <>
                <Button size="sm" colorScheme="green" leftIcon={<BsCheckCircle />} onClick={() => openApprovalAction("APPROVE")}>
                  Approve
                </Button>
                <Button size="sm" colorScheme="red" leftIcon={<BsXCircle />} onClick={() => openApprovalAction("REJECT")}>
                  Reject
                </Button>
                <Button size="sm" colorScheme="orange" leftIcon={<BsArrowCounterclockwise />} onClick={() => openApprovalAction("SEND_BACK")}>
                  Send Back
                </Button>
                <Button size="sm" colorScheme="yellow" leftIcon={<BsLightningChargeFill />} onClick={() => openApprovalAction("BENCH_APPROVE")}>
                  Approve by Bench
                </Button>
              </>
            ) : null}
            {permissions.canWithdraw ? (
              <Button size="sm" variant="outline" onClick={() => withdrawMutation.mutate(id)} isLoading={withdrawMutation.isPending}>
                Withdraw
              </Button>
            ) : null}
            {["APPROVED", "REJECTED"].includes(request?.status) ? (
              <Button size="sm" onClick={() => revisionMutation.mutate(id)} isLoading={revisionMutation.isPending}>
                Create Revision
              </Button>
            ) : null}
            {permissions.canEdit ? (
              <Button size="sm" variant="outline" onClick={saveDraft} isLoading={saveMutation.isPending}>
                Save Draft
              </Button>
            ) : null}
            {permissions.canSubmit ? (
              <Button size="sm" colorScheme="blue" onClick={submitDialog.onOpen}>
                Submit Review
              </Button>
            ) : null}
          </HStack>
        </HStack>
      </RiskReviewPanel>
        {validationSummary.length ? (
          <Alert status="error" borderRadius="md" alignItems="flex-start">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">Review {validationSummary.length} fields before submission.</Text>
              {validationSummary.map((item, index) => (
                <Text key={index} fontSize="sm">
                  {item.message}
                </Text>
              ))}
            </Box>
          </Alert>
        ) : null}
        <Grid templateColumns={{ base: "1fr", xl: "minmax(0, 1fr) 340px" }} gap={{ base: 5, xl: 6 }}>
          <GridItem>
            <HStack justify="flex-end" mb={3}>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<BsMagic />}
                onClick={quickFillTestCase}
                isDisabled={!permissions.canEdit}
              >
                Quick Fill Testcase
              </Button>
            </HStack>
            <RiskReviewDynamicForm
              template={template}
              form={form}
              editable={permissions.canEdit}
              requestId={id}
            />
            {!hasDeclarationInTemplate ? (
              <RiskReviewPanel p={5} mt={5}>
                <Text fontWeight="semibold" mb={2}>
                  Declaration
                </Text>
                <Text color="gray.500" mb={3}>
                  I confirm that the information provided in this Risk Review is complete and the identified risks and mitigation actions have been reviewed.
                </Text>
                <Checkbox
                  isChecked={declarationAccepted}
                  isDisabled={!permissions.canEdit}
                  onChange={(event) => setDeclarationAccepted(event.target.checked)}
                >
                  I confirm the declaration above.
                </Checkbox>
              </RiskReviewPanel>
            ) : null}
            <RiskReviewWorkflowSelection
              request={request}
              selectedWorkflowId={selectedWorkflowId}
              onChange={setSelectedWorkflowId}
              disabled={!permissions.canEdit}
            />
          </GridItem>
          <GridItem>
            <VStack align="stretch" spacing={3}>
              <RiskReviewRequestSummary request={request} template={template} values={liveValues} />
              <RiskReviewRiskSummary risks={liveRisks} />
            </VStack>
          </GridItem>
        </Grid>
        <RiskReviewApprovalTimeline approvals={runtime?.approvals || []} />
      <RiskReviewConfirmDialog
        isOpen={submitDialog.isOpen}
        onClose={submitDialog.onClose}
        title="Submit Risk Review?"
        body="The review will be validated and routed for approval. You will no longer be able to edit this draft unless the workflow permits it."
        confirmLabel="Submit Review"
        isLoading={saveMutation.isPending || submitMutation.isPending}
        onConfirm={submitReview}
      />
      <RiskReviewConfirmDialog
        isOpen={actionDialog.isOpen}
        onClose={actionDialog.onClose}
        title={{ APPROVE: "Approve Risk Review?", REJECT: "Reject Risk Review?", SEND_BACK: "Send Risk Review Back?", BENCH_APPROVE: "Approve Risk Review by Bench?" }[approvalAction] || "Confirm action"}
        body={(
          <Box>
            {approvalAction === "BENCH_APPROVE" ? <Text mb={3}>This will approve the request and override all remaining approval stages.</Text> : null}
            <Textarea value={approvalComments} onChange={(event) => setApprovalComments(event.target.value)} placeholder={approvalAction === "REJECT" || approvalAction === "SEND_BACK" ? "Comments are recommended" : "Comments (optional)"} />
          </Box>
        )}
        confirmLabel={{ APPROVE: "Approve", REJECT: "Reject", SEND_BACK: "Send Back", BENCH_APPROVE: "Approve by Bench" }[approvalAction] || "Confirm"}
        colorScheme={approvalAction === "REJECT" ? "red" : approvalAction === "SEND_BACK" ? "orange" : approvalAction === "BENCH_APPROVE" ? "yellow" : "green"}
        isLoading={approvalMutation.isPending}
        onConfirm={() => approvalMutation.mutate({ action: approvalAction, comments: approvalComments })}
      />
    </RiskReviewPageShell>
  );
};

export default RiskReviewRequestDetailPage;
